// src/jsx/pages/users/UserTable.js

import { Button, Empty, Input, Popconfirm } from 'antd';
import React, { useMemo, useState } from 'react';
import { useGlobalFilter, useSortBy, useTable } from 'react-table';

/**
 * GlobalFilter component for user search input.
 */
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <Input
      placeholder="Buscar usuarios..."
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      style={{ marginBottom: "0px", width: "50%", minWidth: "250px" }}
    />
  );
};

/**
 * Tabla de usuarios usando react-table.
 */
const UserTable = ({ users, loading, onEdit, onDelete }) => {
  // Estado para controlar la cantidad de filas visibles (paginación simple)
  const [visibleRows, setVisibleRows] = useState(25);

  // Definición de columnas para react-table (debe tener 'Header' y 'accessor')
  const columns = useMemo(() => [
    {
      Header: 'Nombre',
      accessor: 'name',
    },
    {
      Header: 'Email',
      accessor: 'email',
    },
    {
      Header: 'Rol',
      accessor: 'role',
      Cell: ({ value }) => (value === 'admin' ? 'Empresa' : 'Super Admin'),
    },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => (value === 'ACTIVE' ? 'Activo' : 'Inactivo'),
    },
    {
      Header: 'Compania',
      accessor: row => row.company?.name ?? '',
      id: 'company', // Se requiere un id si accessor es función
    },
    {
      Header: 'Acciones',
      id: 'actions',
      Cell: ({ row }) => (
        <>
          {/* Solo mostrar el botón "Editar" si el usuario tiene un objeto company */}
          {row.original.company && (
            <Button type="link" onClick={() => onEdit(row.original)}>
              Editar
            </Button>
          )}
          <Popconfirm
            title="¿Estás seguro de eliminar este usuario?"
            onConfirm={() => onDelete(row.original)}
          >
            <Button type="link" danger>
              Eliminar
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ], [onEdit, onDelete]);

  // Configuración de la tabla con react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: users,
      initialState: { pageSize: 25 },
    },
    useGlobalFilter,
    useSortBy
  );

  // Filtrado de filas para paginación simple
  const filteredRows = useMemo(() => rows.slice(0, visibleRows), [rows, visibleRows]);

  // Maneja el scroll para cargar más filas (infinite scroll)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop < clientHeight + 10) {
      setVisibleRows((prev) => prev + 25);
    }
  };

  return (
    <>
    <div  style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#fff",
                padding: "10px",
              }}>
      <GlobalFilter
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      </div>
      {users.length === 0 ? (
        <Empty style={{ marginTop: "10%" }} description="No hay usuarios cargados." />
      ) : (
        <div
          className="card-body"
          onScroll={handleScroll}
          style={{ height: "700px", overflowY: "auto", padding: "0px" }}
        >
          <div className="dataTables_wrapper">
            <table {...getTableProps()} className="ant-table ant-table-bordered" style={{ width: "100%" }}>
              <thead style={{
                  position: "sticky",
                  top: "0px",
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()} style={{padding:"10px"}}>
                    {headerGroup.headers.map(column => (
                      <th {...column.getHeaderProps()}  style={{padding:"10px"}}>{column.render('Header')}</th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {filteredRows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}  style={{padding:"10px"}}>
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()}  style={{padding:"10px"}}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default UserTable;
